import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  CardMedia,
  Collapse,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import PieChartIcon from "@mui/icons-material/PieChart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BarChartIcon from "@mui/icons-material/BarChart";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InventoryIcon from "@mui/icons-material/Inventory";
import PeopleIcon from "@mui/icons-material/People";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { useNavigate } from "react-router-dom";
import logo from "./images/logo_header.png";
import useUserStore from "../../hooks/useUserStore";
import { authService } from "../../utils/authService";
import { switchLanguage } from "../../i18n";
import axiosInstance from "../../utils/axiosConfig";

function Header({ onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const isAuthenticated = authService.isAuthenticated();
  const user = useUserStore((state) => state.user);
  const userName = isAuthenticated && user ? user.name : "";
  const navigate = useNavigate();
  const [landingOptions, setLandingOptions] = useState();

  // Configuración de i18n
  const { t, i18n: i18next } = useTranslation();
  const toggleLanguage = () => {
    const newLang = i18next.language === "en" ? "es" : "en";
    switchLanguage(newLang);
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate("/schedule");
    } else {
      navigate("/");
    }
  };

  const fetchLandingOptions = async () => {
    try {
      const response = await axiosInstance.get("/landing_options");
      setLandingOptions(response.data);
    } catch (error) {
      console.error("Error fetching landing options:", error);
    }
  };

  const getMenuItems = () => {
    const userRole = user?.role;

    const adminMenuItems = [
      { text: "Schedule", path: "/schedule", icon: <EditCalendarIcon /> },
      { text: "Inventory", path: "/inventory-list", icon: <InventoryIcon /> },
      {
        text: "Services Manager",
        path: "/service-offerings",
        icon: <ImageSearchIcon />,
      },
      {
        text: "Cotizaciones",
        path: "/cotizaciones",
        icon: <RequestQuoteIcon />,
      },
      {
        text: "Questions List",
        path: "/questions-list",
        icon: <FactCheckIcon />,
      },
      { text: "Reports", path: "/reports", icon: <AssessmentIcon /> },
      {
        text: "Dashboard",
        subItems: [
          {
            text: "Services Dashboard",
            path: "/dashboard/services",
            icon: <PieChartIcon />,
          },
          {
            text: "Status Dashboard",
            path: "/dashboard/status",
            icon: <BarChartIcon />,
          },
        ],
        icon: <DashboardIcon />,
        isExpandable: true,
      },
      { text: "User List", path: "/user-list", icon: <PersonIcon /> },
      { text: "Clients", path: "/client-list", icon: <PeopleIcon /> },
      { text: "Logout", path: null, icon: <LogoutIcon />, action: onLogout },
    ];

    const userMenuItems = [
      { text: "Schedule", path: "/schedule", icon: <EditCalendarIcon /> },
      { text: "Clients", path: "/client-list", icon: <PeopleIcon /> },
      { text: "Logout", path: null, icon: <LogoutIcon />, action: onLogout },
    ];

    return userRole === "admin" ? adminMenuItems : userMenuItems;
  };

  const menuItems = getMenuItems();
  const toggleMenu = () => setMenuOpen(!menuOpen);

  useEffect(() => {
    !isAuthenticated && fetchLandingOptions();
  }, []);

  return (
    <>
      <AppBar position="static">
        <Toolbar sx={{ minHeight: "90px !important" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              cursor: "pointer",
            }}
            onClick={handleLogoClick}
          >
            <CardMedia
              component="img"
              image={logo}
              alt="Logo"
              sx={{ width: "100px", height: "80px", mr: 2 }}
            />
            {isAuthenticated && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  background:
                    "linear-gradient(45deg, primary.light, primary.main)",
                  padding: "6px 16px",
                  borderRadius: "20px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  {t("welcome", { name: userName })}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Language Switcher */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton color="inherit" onClick={toggleLanguage}>
              <Typography variant="body1">
                {i18next.language === "en" ? "ES" : "EN"}
              </Typography>
            </IconButton>
          </Box>

          {isAuthenticated ? (
            <IconButton color="inherit" onClick={toggleMenu}>
              <MenuIcon sx={{ fontSize: 35 }} />
            </IconButton>
          ) : (
            <>
              <IconButton color="inherit" href="/login">
                <PersonIcon sx={{ fontSize: 35 }} />
              </IconButton>
              {landingOptions?.find(
                (x) => x.key === "whatsapp" && x.active === true
              ) && (
                <IconButton
                  href={
                    landingOptions?.find(
                      (x) => x.key === "whatsapp" && x.active === true
                    ).value
                  }
                  target="_blank"
                  color="inherit"
                >
                  <WhatsAppIcon sx={{ fontSize: 35 }} />
                </IconButton>
              )}
              {landingOptions?.find(
                (x) => x.key === "instagram" && x.active === true
              ) && (
                <IconButton
                  href={
                    landingOptions?.find(
                      (x) => x.key === "instagram" && x.active === true
                    ).value
                  }
                  target="_blank"
                  color="inherit"
                >
                  <InstagramIcon sx={{ fontSize: 35 }} />
                </IconButton>
              )}
              {landingOptions?.find(
                (x) => x.key === "linkedin" && x.active === true
              ) && (
                <IconButton
                  href={
                    landingOptions?.find(
                      (x) => x.key === "linkedin" && x.active === true
                    ).value
                  }
                  target="_blank"
                  color="inherit"
                >
                  <LinkedInIcon sx={{ fontSize: 35 }} />
                </IconButton>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={menuOpen} onClose={toggleMenu}>
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleMenu}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{t("menu")}</Typography>
          </Box>
          <Divider />
          <List>
            {menuItems.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  button
                  onClick={(event) => {
                    if (item.isExpandable) {
                      event.stopPropagation();
                      setDashboardOpen(!dashboardOpen);
                    } else {
                      item.action ? item.action() : navigate(item.path);
                    }
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "primary.light",
                      color: "white",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "primary.main" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={t(item.text)} />
                  {item.isExpandable &&
                    (dashboardOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItem>
                {item.subItems && (
                  <Collapse in={dashboardOpen}>
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem, subIndex) => (
                        <ListItem
                          button
                          key={subIndex}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(subItem.path);
                          }}
                          sx={{
                            pl: 4,
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "white",
                            },
                          }}
                        >
                          <ListItemIcon sx={{ color: "primary.main" }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText primary={t(subItem.text)} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default Header;
